import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { debounce } from 'lodash';
import {
  TextField, Button, Typography, Container,
  Autocomplete, CircularProgress, Grid,
} from '@mui/material';
import FormCard from './FormCard';
import { actionButtonSx, pageTitleSx } from '../styles/uiTokens';

function FuncionarioForm({ onFuncionarioAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [id, setId] = useState(null);
  const [pessoaId, setPessoaId] = useState('');
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [loadingPessoa, setLoadingPessoa] = useState(false);
  const [cbo, setCbo] = useState('');
  const [ctps, setCtps] = useState('');
  const [cargo, setCargo] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [chapa, setChapa] = useState('');
  const [horasSemanais, setHorasSemanais] = useState('');
  const [horarioInicio, setHorarioInicio] = useState('');
  const [horarioFim, setHorarioFim] = useState('');
  const [intervaloInicio, setIntervaloInicio] = useState('');
  const [intervaloFim, setIntervaloFim] = useState('');
  const [dataAdmissao, setDataAdmissao] = useState('');
  const [dataRescisao, setDataRescisao] = useState('');
  const [salario, setSalario] = useState('');

  const cancelButtonSx = {
    mt: 2
  };

  const debouncedFetchPessoas = useMemo(
    () => debounce((searchTerm) => {
      setLoadingPessoa(true);
      api.get('/pessoas', { params: { search: searchTerm, limit: 50 } })
        .then((response) => setPessoaOptions(response.data.data || []))
        .catch((err) => console.error('Erro ao buscar pessoas:', err))
        .finally(() => setLoadingPessoa(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchPessoas.cancel();
    };
  }, [debouncedFetchPessoas]);

  useEffect(() => {
    if (initialData) {
      setId(initialData.id);
      setPessoaId(initialData.pessoa_id || '');
      setSelectedPessoa(initialData.pessoa_nome ? { id: initialData.pessoa_id, nome: initialData.pessoa_nome } : null);
      setCbo(initialData.cbo || '');
      setCtps(initialData.ctps || '');
      setCargo(initialData.cargo || '');
      setDepartamento(initialData.departamento || '');
      setChapa(initialData.chapa || '');
      setHorasSemanais(initialData.horas_semanais != null ? String(initialData.horas_semanais) : '');
      setHorarioInicio(initialData.horario_inicio || '');
      setHorarioFim(initialData.horario_fim || '');
      setIntervaloInicio(initialData.intervalo_inicio || '');
      setIntervaloFim(initialData.intervalo_fim || '');
      setDataAdmissao(initialData.data_admissao ? String(initialData.data_admissao).substring(0, 10) : '');
      setDataRescisao(initialData.data_rescisao ? String(initialData.data_rescisao).substring(0, 10) : '');
      setSalario(initialData.salario != null ? String(initialData.salario) : '');
    } else {
      clearForm();
    }
  }, [initialData]);

  const clearForm = () => {
    setId(null);
    setPessoaId('');
    setSelectedPessoa(null);
    setPessoaOptions([]);
    setCbo('');
    setCtps('');
    setCargo('');
    setDepartamento('');
    setChapa('');
    setHorasSemanais('');
    setHorarioInicio('');
    setHorarioFim('');
    setIntervaloInicio('');
    setIntervaloFim('');
    setDataAdmissao('');
    setDataRescisao('');
    setSalario('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const dados = {
      pessoa_id: parseInt(pessoaId, 10),
      cbo, ctps, cargo, departamento, chapa,
      horas_semanais: horasSemanais || null,
      horario_inicio: horarioInicio || null,
      horario_fim: horarioFim || null,
      intervalo_inicio: intervaloInicio || null,
      intervalo_fim: intervaloFim || null,
      data_admissao: dataAdmissao || null,
      data_rescisao: dataRescisao || null,
      salario: salario || null,
    };
    if (user?.tipo_usuario === 'admin' && user?.empresa_id) {
      dados.empresa_id = user.empresa_id;
    }

    const request = id
      ? api.put('/funcionarios/' + id, dados)
      : api.post('/funcionarios', dados);

    request
      .then(() => {
        clearForm();
        onFuncionarioAdded();
        if (onCancelEdit) onCancelEdit();
      })
      .catch((error) => {
        const msg = error.response?.data?.message || 'Erro ao salvar funcionário.';
        console.error(msg, error);
      });
  };

  return (
    <Container maxWidth="sm">
      <FormCard component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ ...pageTitleSx, mb: 1 }}>
          {id ? 'Editar Funcionário' : 'Novo Funcionário'}
        </Typography>

        <Autocomplete
          options={pessoaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPessoa}
          onChange={(event, newValue) => {
            setSelectedPessoa(newValue);
            setPessoaId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchPessoas(newInputValue);
              else setPessoaOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pessoa"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoa ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="CBO" fullWidth value={cbo} onChange={(e) => setCbo(e.target.value)} margin="normal" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="CTPS" fullWidth value={ctps} onChange={(e) => setCtps(e.target.value)} margin="normal" />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Cargo" fullWidth value={cargo} onChange={(e) => setCargo(e.target.value)} margin="normal" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Departamento" fullWidth value={departamento} onChange={(e) => setDepartamento(e.target.value)} margin="normal" />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Chapa" fullWidth value={chapa} onChange={(e) => setChapa(e.target.value)} margin="normal" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Horas Semanais" fullWidth type="number" value={horasSemanais} onChange={(e) => setHorasSemanais(e.target.value)} margin="normal" inputProps={{ step: 0.5, min: 0 }} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Horário Início" fullWidth type="time" value={horarioInicio} onChange={(e) => setHorarioInicio(e.target.value)} margin="normal" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Horário Fim" fullWidth type="time" value={horarioFim} onChange={(e) => setHorarioFim(e.target.value)} margin="normal" InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Intervalo Início" fullWidth type="time" value={intervaloInicio} onChange={(e) => setIntervaloInicio(e.target.value)} margin="normal" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Intervalo Fim" fullWidth type="time" value={intervaloFim} onChange={(e) => setIntervaloFim(e.target.value)} margin="normal" InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Data Admissão" fullWidth type="date" value={dataAdmissao} onChange={(e) => setDataAdmissao(e.target.value)} margin="normal" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Data Rescisão" fullWidth type="date" value={dataRescisao} onChange={(e) => setDataRescisao(e.target.value)} margin="normal" InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>

        <TextField label="Salário" fullWidth type="number" value={salario} onChange={(e) => setSalario(e.target.value)} margin="normal" inputProps={{ step: 0.01, min: 0 }} />

        <Button type="submit" variant="contained" sx={{ ...actionButtonSx, mt: 2, mr: 2 }}>
          {id ? 'Atualizar' : 'Adicionar'}
        </Button>
        <Button type="button" variant="outlined" color="secondary" sx={cancelButtonSx} onClick={onCancelEdit}>
          Cancelar
        </Button>
      </FormCard>
    </Container>
  );
}

export default FuncionarioForm;
